/*!
 * AudioSyncOffset — how much to shift a separately recorded audio track so it
 * lines up with the audio already in a video.
 *
 * The job: someone films a performance on a phone and records the SAME
 * performance on a second device placed properly (the camera's own track is
 * then only a rough witness of what happened when). Aligning the good take
 * with the picture by hand means reading two timestamps and doing arithmetic;
 * both signals already carry the answer.
 *
 * WHAT WAS MEASURED (2026-09-17, 72 true pairs + 18 aliens built from the
 * tracked Salamander piano samples degraded into "camera mic" versions — three
 * mic distances, 6…50 s excerpts, AAC 32…96 kbps, SNR 8…32 dB; bench:
 * tests/playwright/audio-sync-offset-bench.mjs):
 *
 *   FINDING the lag — onset strength wins, and not narrowly:
 *     onset envelope  71/72     RMS envelope  50/72     sum of both  70/72
 *   The RMS envelope collapses exactly where it matters — a short clip of one
 *   camera angle (6–10 s): 5/24 against onset's 23/24.
 *
 *   VERIFYING the lag — the finder's own peak height is worthless, because an
 *   onset curve is spiky and its peak z-score runs high for ANY pair: aliens
 *   scored z 6.4…17.5, overlapping the true range. What separates cleanly is
 *   the Pearson correlation of the two RMS envelopes over their OVERLAP at the
 *   found lag:
 *     true + correct   min -0.07   p10 0.25   median 0.54
 *     true + wrong     max  0.10
 *     alien            max  0.25   (an unrelated take of the same instrument)
 *   Hence r >= 0.30 → confident, 0.15…0.30 → applied but flagged, below →
 *   refuse and say so. At r >= 0.30: 61/72 accepted, ZERO wrong, ZERO aliens.
 *   The flagged band is deliberately generous: it recovers 7 of the 11 correct
 *   answers that 0.30 refuses, and admits 3 of 18 aliens — which is exactly
 *   what "flagged" is for, now that the page previews the result out loud.
 *
 *   Rejected by measurement — do not re-try without new numbers:
 *     · GCC-PHAT on the waveform — as accurate but 10–25x the cost, and it
 *       needs the whole file in memory at audio rate.
 *     · a PHAT "refinement" pass around the coarse lag — made it WORSE
 *       (p90 of the error went 20 ms → 6.3 s).
 *     · log envelopes, moving-average-detrended envelopes, 4-band envelopes —
 *       all at or below plain RMS for more work.
 *
 * Offset sign: `offsetSec` is what the SECOND signal (the replacement) must be
 * delayed by. Positive = pad its start with silence; negative = cut that much
 * from its head. It is exactly (time of an event in the video) minus (time of
 * the same event in the replacement).
 */
(function (global) {
    'use strict';

    var HOP_SEC = 0.010;          // 10 ms — the precision of the answer
    var MIN_FRAMES = 100;         // under 1 s of material is not an alignment problem
    var MIN_OVERLAP_SEC = 3;
    var R_STRONG = 0.30, R_WEAK = 0.15;

    /* ── FFT (radix-2, in place) ──────────────────────────────────────── */
    function fft(re, im, inverse) {
        var n = re.length, i, j, bit, len, ang, wr, wi, k, cr, ci, ur, ui, vr, vi, ncr, t;
        for (i = 1, j = 0; i < n; i++) {
            for (bit = n >> 1; j & bit; bit >>= 1) j ^= bit;
            j ^= bit;
            if (i < j) { t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; }
        }
        for (len = 2; len <= n; len <<= 1) {
            ang = (inverse ? 2 : -2) * Math.PI / len;
            wr = Math.cos(ang); wi = Math.sin(ang);
            for (i = 0; i < n; i += len) {
                cr = 1; ci = 0;
                for (k = 0; k < len / 2; k++) {
                    ur = re[i + k]; ui = im[i + k];
                    vr = re[i + k + len / 2] * cr - im[i + k + len / 2] * ci;
                    vi = re[i + k + len / 2] * ci + im[i + k + len / 2] * cr;
                    re[i + k] = ur + vr; im[i + k] = ui + vi;
                    re[i + k + len / 2] = ur - vr; im[i + k + len / 2] = ui - vi;
                    ncr = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = ncr;
                }
            }
        }
        if (inverse) for (i = 0; i < n; i++) { re[i] /= n; im[i] /= n; }
    }
    function nextPow2(n) { var p = 1; while (p < n) p <<= 1; return p; }

    function zeroMean(a) {
        var m = 0, i, n = a.length, o = new Float64Array(n);
        for (i = 0; i < n; i++) m += a[i];
        m /= (n || 1);
        for (i = 0; i < n; i++) o[i] = a[i] - m;
        return o;
    }

    /** Full cross-correlation; index i corresponds to lag (i - (b.length - 1)) frames. */
    function crossCorr(a, b) {
        var n = nextPow2(a.length + b.length), i, rr, ri;
        var ar = new Float64Array(n), ai = new Float64Array(n);
        var br = new Float64Array(n), bi = new Float64Array(n);
        ar.set(a);
        for (i = 0; i < b.length; i++) br[i] = b[b.length - 1 - i];
        fft(ar, ai, false); fft(br, bi, false);
        for (i = 0; i < n; i++) {
            rr = ar[i] * br[i] - ai[i] * bi[i];
            ri = ar[i] * bi[i] + ai[i] * br[i];
            ar[i] = rr; ai[i] = ri;
        }
        fft(ar, ai, true);
        return ar;
    }

    /* ── features ─────────────────────────────────────────────────────── */
    /** RMS envelope + onset strength, both on the same 10 ms grid. */
    function featuresFromMono(mono, sampleRate) {
        var hop = Math.max(1, Math.round(sampleRate * HOP_SEC));
        var n = Math.floor(mono.length / hop);
        var rms = new Float64Array(n), onset = new Float64Array(n);
        var i, j, s, off, v;
        for (i = 0; i < n; i++) {
            s = 0; off = i * hop;
            for (j = 0; j < hop; j++) { v = mono[off + j] || 0; s += v * v; }
            rms[i] = Math.sqrt(s / hop);
        }
        // onset = the positive part of the log-envelope's first difference: the
        // gain-invariant "something started here" signal. Gain invariance is the
        // point — a phone's automatic level control rewrites loudness, not onsets.
        var prev = Math.log10(Math.max(rms[0] || 0, 1e-6)), cur, d;
        for (i = 1; i < n; i++) {
            cur = Math.log10(Math.max(rms[i], 1e-6));
            d = cur - prev;
            onset[i] = d > 0 ? d : 0;
            prev = cur;
        }
        return { rms: rms, onset: onset, hop: HOP_SEC, frames: n, duration: n * HOP_SEC };
    }

    /** Mix an AudioBuffer down to mono and extract features. */
    function features(audioBuffer) {
        var chs = audioBuffer.numberOfChannels, len = audioBuffer.length;
        var mono = new Float32Array(len), c, i, data;
        for (c = 0; c < chs; c++) {
            data = audioBuffer.getChannelData(c);
            for (i = 0; i < len; i++) mono[i] += data[i] / chs;
        }
        return featuresFromMono(mono, audioBuffer.sampleRate);
    }

    /** Pearson r between A and B where B[i] lines up with A[i + offFrames]. */
    function overlapPearson(A, B, offFrames) {
        var lo = Math.max(0, -offFrames), hi = Math.min(B.length, A.length - offFrames);
        var n = hi - lo, i, ma = 0, mb = 0, sab = 0, sa = 0, sb = 0, da, db;
        if (n < 20) return { r: 0, frames: 0 };
        for (i = lo; i < hi; i++) { ma += A[i + offFrames]; mb += B[i]; }
        ma /= n; mb /= n;
        for (i = lo; i < hi; i++) {
            da = A[i + offFrames] - ma; db = B[i] - mb;
            sab += da * db; sa += da * da; sb += db * db;
        }
        return { r: sab / (Math.sqrt(sa * sb) || 1e-12), frames: n };
    }

    /**
     * @param {{rms:Float64Array,onset:Float64Array}} a features of the VIDEO's own audio
     * @param {{rms:Float64Array,onset:Float64Array}} b features of the REPLACEMENT
     * @returns {{verdict:string, offsetSec:number, r:number, overlapSec:number, peak:number}}
     *          verdict: 'strong' | 'weak' | 'none' | 'too-short'
     */
    function estimate(a, b) {
        if (!a || !b || !a.onset || !b.onset ||
            a.onset.length < MIN_FRAMES || b.onset.length < MIN_FRAMES) {
            return { verdict: 'too-short', offsetSec: 0, r: 0, overlapSec: 0, peak: 0 };
        }
        var A = zeroMean(a.onset), B = zeroMean(b.onset);
        var c = crossCorr(A, B);
        var len = A.length + B.length - 1, i, bi = 0, bv = -Infinity;
        for (i = 0; i < len; i++) if (c[i] > bv) { bv = c[i]; bi = i; }
        var offFrames = bi - (B.length - 1);
        var ver = overlapPearson(a.rms, b.rms, offFrames);
        var overlapSec = ver.frames * HOP_SEC;
        var verdict = 'none';
        if (overlapSec >= MIN_OVERLAP_SEC) {
            if (ver.r >= R_STRONG) verdict = 'strong';
            else if (ver.r >= R_WEAK) verdict = 'weak';
        }
        return {
            verdict: verdict,
            offsetSec: offFrames * HOP_SEC,
            r: ver.r,
            overlapSec: overlapSec,
            peak: bv
        };
    }

    var api = {
        HOP_SEC: HOP_SEC,
        R_STRONG: R_STRONG,
        R_WEAK: R_WEAK,
        features: features,
        featuresFromMono: featuresFromMono,
        estimate: estimate,
        _overlapPearson: overlapPearson
    };
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    global.AudioSyncOffset = api;
})(typeof window !== 'undefined' ? window : globalThis);
